import { Button, Tag } from "antd";
import { useGetCascadeLogQuery } from "api/TenantSlug/Processes/hooks";
import { cascadeProcessItemStatusColorMap } from "components/Tables/CascadeLogTable/cascadeProcessItemStatusColorMap";
import { formatCascadeLogDuration } from "components/Tables/CascadeLogTable/formatCascadeLogDuration";
import { CONVERSION_STATE_EID_FILTER_NAME } from "components/Tables/IntegrationStepLogTable/IntegrationStepLogTable";
import { Drawer } from "components/UI/Drawer";
import { DrawerCollapsible } from "components/UI/DrawerCollapsible/DrawerCollapsible";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle";
import { PropertiesList } from "components/UI/PropertiesList/PropertiesList";
import { QueryState } from "components/UI/QueryState/QueryState";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { PermissionEnum, SearchFilterEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import CascadeLogData = App.Data.CascadeLogData;

interface CascadeLogDetailDrawerProps {
    logId: string;
}

export const CascadeLogDetailDrawer: FC<CascadeLogDetailDrawerProps> = ({ logId }) => {
    const { t } = usePaldockTranslation("drawers.cascade-log-detail");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGA } = usePaldockTranslation("drawers.general-actions");
    const { t: tEnum } = usePaldockTranslation("general");
    const drawerId = DrawerId.CascadeLogDetail;
    const permissionsUtils = usePermission();

    const colorMap = cascadeProcessItemStatusColorMap;
    const CONVERSION_STATE_EID_FILTER = `${TableId.IntegrationStepLogTable}.${CONVERSION_STATE_EID_FILTER_NAME}.${SearchFilterEnum.Equal}`;

    const getCascadeLogQuery = useGetCascadeLogQuery(logId, {
        filter: {
            include:
                "conversionStateSend,conversionStateSend.owner,conversionStateSend.advertiser,conversionStateSend.offer,cascade,integration",
        },
    });
    const log = getCascadeLogQuery.data?.log;

    const logList = log
        ? PropertiesList.create<CascadeLogData>(log, [
              {
                  term: tG("date"),
                  description: ({ created_at }) => (created_at ? <DateTimeCell value={created_at} /> : <EmptyCell />),
              },
              {
                  term: tG("conversion_id"),
                  description: ({ conversionStateSend }) =>
                      conversionStateSend?.origin_eid ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionStateSend.origin_eid} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("event_id"),
                  description: ({ conversionStateSend }) =>
                      conversionStateSend?.eid ? (
                          <ShortenedIdWithCopy charactersBetweenDots={35} id={conversionStateSend.eid} />
                      ) : (
                          <EmptyCell />
                      ),
              },
              {
                  term: tG("affiliate"),
                  description: ({ conversionStateSend }) => (
                      <EntityLinkCell.Owner entity={conversionStateSend?.owner} />
                  ),
              },
              {
                  term: tG("advertiser"),
                  description: ({ conversionStateSend }) => (
                      <EntityLinkCell.Advertiser entity={conversionStateSend?.advertiser} />
                  ),
              },
              {
                  term: tG("offer"),
                  description: ({ conversionStateSend }) => (
                      <EntityLinkCell.Offer entity={conversionStateSend?.offer} />
                  ),
              },
              {
                  term: t("fields.channel"),
                  description: ({ cascade }) => <EntityLinkCell.Cascade entity={cascade} />,
              },
              {
                  term: t("fields.integration"),
                  description: ({ integration }) => <EntityLinkCell.Integration entity={integration} />,
              },
              {
                  term: t("fields.duration"),
                  description: ({ duration_ms }) => formatCascadeLogDuration(duration_ms) ?? <EmptyCell />,
              },
              {
                  term: t("fields.state"),
                  description: ({ status }) => {
                      if (!status) return <EmptyCell />;
                      const cfg = colorMap[status] ?? { color: "default" };
                      return (
                          <Tag icon={"icon" in cfg ? cfg.icon : undefined} color={cfg.color}>
                              {tEnum(`cascade_process_item_status.${status}`)}
                          </Tag>
                      );
                  },
              },
              {
                  term: t("fields.detail"),
                  description: ({ itemId, message }) => (
                      <StepLogMessageCell message={message} id={`cascade-log-message-${itemId}`} lines={10} />
                  ),
              },
          ])
        : null;

    return (
        <Drawer
            drawerId={drawerId}
            title={<DrawerTitle mainTitle={t("title")} />}
            searchParamsToClean={[`${drawerId}-id`]}
            extraButtons={
                <>
                    {/* TODO: view scenario run - commented out for now */}
                    {/* <Button size="large" disabled>
                        {tGA("view_scenario_run")}
                    </Button> */}
                    {permissionsUtils.hasPermission(PermissionEnum.StepLogIntegrationsView) &&
                        log?.conversionStateSend?.eid && (
                            <TenantLink
                                to="/log/api"
                                search={{ [CONVERSION_STATE_EID_FILTER]: log.conversionStateSend.eid }}
                            >
                                <Button size="large">{tGA("view_step_logs")}</Button>
                            </TenantLink>
                        )}
                </>
            }
        >
            {!log ? (
                <QueryState
                    isLoading={getCascadeLogQuery.isLoading}
                    isError={getCascadeLogQuery.isError || (!getCascadeLogQuery.isLoading && !log)}
                    errorKey="errors.something_went_wrong"
                />
            ) : (
                <DrawerContent className="flex flex-col gap-4 p-4">
                    <DrawerCollapsible title={t("sections.log")}>
                        <PropertiesList list={logList ?? []} />
                    </DrawerCollapsible>
                </DrawerContent>
            )}
        </Drawer>
    );
};
